import { Injectable } from "@angular/core";
import { AppConstants } from "app/core/vo/app-constants";
import { UtilityScripts } from "app/helpers/utility-scripts";
import { QuestionDataVO } from "app/rubrics/adaptive/vo/questiondatavo";
import { StatementDataVO } from "app/rubrics/adaptive/vo/statementDataVO";
import { TrackingManager } from "../tracking-manager";
import { TrackingVO } from "../trackingvo";
import { LaunchDataVO } from "./launchdatavo";
import { LaunchURLVO } from "./launchurlvo";

@Injectable()
export class Statements {
	private readonly STARTED = "started";
	private readonly COMPLETED = "completed";
	private readonly ANSWERED = "answered";
	private readonly TERMINATED = "terminated";
	private readonly PASSED = "passed";
	private trackingVO;
	private launchDataVO;
	private launchURLVO;

	constructor(
		trackingVO: TrackingVO,
		launchDataVO: LaunchDataVO,
		launchURLVO: LaunchURLVO
	) {
		this.trackingVO = trackingVO;
		this.launchDataVO = launchDataVO;
		this.launchURLVO = launchURLVO;
	}
	/**Generic function for creating xapi statements
	 * @param statementDataVO contains generic fields needs to be send in all the statements.
	 * @param questionDataVO contains fields needs to be send in 'answered' statement only.
	 */
	public createStatements(
		statementDataVO: StatementDataVO,
		questionDataVO?: QuestionDataVO
	) {
		let _statement: any;
		let verb = statementDataVO.verb;
		let isCompleted = verb == this.ANSWERED || verb == this.COMPLETED;
		this.trackingVO.sessionTime = UtilityScripts.resolveTime(
			this.trackingVO.sessionTime
		);
		let duration = UtilityScripts.convertMillisecondsToISO8601Duration(
			UtilityScripts.convertToMS(this.trackingVO.sessionTime)
		);
		let clientid = this.launchDataVO.clientKey
			? this.launchDataVO.clientKey
			: null;
		let updatedActor = JSON.parse(this.launchURLVO.actor);
		updatedActor.name = updatedActor.account.name;
		_statement = {
			id: "",
			actor: updatedActor,
			verb: {
				id: "http://adlnet.gov/expapi/verbs/" + verb,
				display: {
					"en-US": verb,
				},
			},
			timestamp: this.getTimeStamp(),
			object: {
				id: this.launchURLVO.activityId,
				objectType: "Activity",
			},
			context: {
				registration: this.launchURLVO.registration,
				contextActivities: this.getContextActivitiesJson(statementDataVO, verb),
				extensions: this.getContextExtensionsJSON(),
			},
		};

		if (statementDataVO.statementType === "defined") {
			if (verb === this.COMPLETED) {
				_statement.result = {
					completion: isCompleted,
					duration: duration,
				};
			} else if (verb === this.PASSED) {
				if (this.trackingVO.totalScore != 0) {
					_statement.result = {
						success: true,
						duration: duration,
						score: {
							scaled: parseFloat(
								(
									this.trackingVO.totalScore / this.trackingVO.maxTotalScore
								).toFixed(2)
							),
						},
					};
				} else {
					_statement.result = {
						success: true,
						duration: duration,
					};
				}
			} else if (verb === this.TERMINATED) {
				_statement.result = {
					duration: duration,
				};
			}
		} else {
			_statement.context.revision =
				statementDataVO.courseVersion + "|" + this.courseCustomizerVersion();
			_statement.context.language = statementDataVO.currentLanguage;
			if (verb === this.ANSWERED) {
				_statement.result = {
					success: questionDataVO.result,
					response: questionDataVO.response,
					duration: duration,
				};
				questionDataVO.choices.forEach((choice) => {
					choice.id =
						AppConstants.NAVEX_URL +
						statementDataVO.uniqueId +
						"/" +
						statementDataVO.courseRevision +
						"/" +
						clientid +
						"/" +
						statementDataVO.moduleId +
						"/" +
						statementDataVO.pageId +
						"/" +
						questionDataVO.questionId +
						"/" +
						choice.id;
				});
				_statement.object.definition = {
					description: {
						"en-US": questionDataVO.instruction,
						id:
							AppConstants.NAVEX_URL +
							statementDataVO.uniqueId +
							"/" +
							statementDataVO.courseRevision +
							"/" +
							clientid +
							"/" +
							statementDataVO.moduleId +
							"/" +
							statementDataVO.pageId +
							"/" +
							questionDataVO.questionId,
					},
					type: "http://adlnet.gov/expapi/activities/cmi.interaction",
					interactionType: "choice",
					choices: questionDataVO.choices,
					correctResponsesPattern: [questionDataVO.correctResponsesPattern],
				};
			} else if (verb === this.STARTED || verb === this.COMPLETED) {
				if (verb === this.COMPLETED) {
					if (
						statementDataVO.isModule &&
						this.trackingVO.currentModuleMaxScore
					) {
						_statement.result = {
							completion: true,
							duration: duration,
							score: {
								scaled: parseFloat(
									(
										this.trackingVO.currentModuleScore /
										this.trackingVO.currentModuleMaxScore
									).toFixed(2)
								),
							},
						};
						this.trackingVO.currentModuleScore = 0;
						this.trackingVO.currentModuleMaxScore = 0;
					} else {
						_statement.result = {
							completion: true,
							duration: duration,
						};
					}
				}
				_statement.object.definition = {
					name: {
						"en-US": statementDataVO.isModule
							? statementDataVO.moduleId
							: statementDataVO.pageId,
					},
					type: statementDataVO.isModule
						? "http://adlnet.gov/expapi/activities/module"
						: "http://adlnet.gov/expapi/activities/page",
				};
			}
		}
		// Update time to get each action that will send a message to have action-specific duration, rather than page-specific duration
		TrackingManager.instance.updateBookmarkedTime();
		return _statement;
	}

	/**
	 * Standardized function that builds the "context.extensions" block of xAPI messages.
	 * @returns JSON of context extensions for xAPI messages
	 */
	private getContextExtensionsJSON() {
		let sessionextension = this.launchDataVO.sessionid;
		let sessionheader = Object.keys(sessionextension)[0];
		let sessionvalue = sessionextension[sessionheader];
		let clientid = this.launchDataVO.clientKey
			? this.launchDataVO.clientKey
			: null;
		let launchurl = this.launchURLVO.activityId;
		let masteryScore = this.launchDataVO.masteryScore;

		let extensionsvalue = JSON.parse(
			`{ "${sessionheader}": "${sessionvalue}", "https://navexone.com/clientkey": "${clientid}", "https://navexone.com/launchurl": "${launchurl}", "https://w3id.org/xapi/cmi5/context/extensions/masteryscore": ${masteryScore}}`
		);
		return extensionsvalue;
	}
	/**
	 * Standardized function that builds the "context.contextActivities" block of xAPI messages.
	 * @returns JSON of context contextActivities for xAPI messages
	 */
	private getContextActivitiesJson(statementDataVO, verb) {
		let contextActivitiesJson: any;
		if (statementDataVO.statementType === "defined") {
			contextActivitiesJson = {
				grouping: this.launchDataVO.contextActivities.grouping,
				category: [
					{
						id: "https://w3id.org/xapi/cmi5/context/categories/cmi5",
						objectType: "Activity",
					},
				],
			};
			if (verb === this.COMPLETED || verb === this.PASSED) {
				contextActivitiesJson.category.push({
					id: "https://w3id.org/xapi/cmi5/context/categories/moveon",
					objectType: "Activity",
				});
			}
			if(verb === this.TERMINATED) {
				contextActivitiesJson.other = [{
					definition:{
						name: {"en-US": "language"},
						description:{
							"en-US":TrackingManager.instance.lessonLocation[0]
						}},
						id:AppConstants.NAVEX_URL +	statementDataVO.uniqueId + "/" + statementDataVO.courseRevision + "/" +	this.launchDataVO.clientKey, // Course ID on its own
						objectType: "Activity"
				},{
					definition:{
						name: {"en-US": "mode"},
						description:{
							"en-US":TrackingManager.instance.lessonLocation[1]
						}},
						id:AppConstants.NAVEX_URL +	statementDataVO.uniqueId + "/" + statementDataVO.courseRevision + "/" +	this.launchDataVO.clientKey, // Course ID on its own
						objectType: "Activity"
				},{
					definition:{
						name: {"en-US": "location"},
						description:{
							"en-US":this.trackingVO.geoLocation
						}},
						id:AppConstants.NAVEX_URL +	statementDataVO.uniqueId + "/" + statementDataVO.courseRevision + "/" +	this.launchDataVO.clientKey, // Course ID on its own
						objectType: "Activity"
				}]
			}
		} else {
			if (statementDataVO.isModule) {
				// Module block
				contextActivitiesJson = {
					parent: [this.getActivitiesContent(statementDataVO).course],
				};
				if (statementDataVO.verb === this.STARTED) {
					contextActivitiesJson.other = [{
						definition:{
							name: {"en-US": "complianceTypeID"},
							description:{
								"en-US":this.trackingVO.complianceTypeID
							}},
							id:AppConstants.NAVEX_URL +	statementDataVO.uniqueId + "/" + statementDataVO.courseRevision + "/" +	this.launchDataVO.clientKey, // Course ID on its own
							objectType: "Activity"
					},{
						definition:{
							name: {"en-US": "complianceTypeName"},
							description:{
								"en-US":this.trackingVO.complianceTypeName
							}},
							id:AppConstants.NAVEX_URL +	statementDataVO.uniqueId + "/" + statementDataVO.courseRevision + "/" +	this.launchDataVO.clientKey, // Course ID on its own
							objectType: "Activity"
					},{
						definition:{
							name: {"en-US": "accessibilityOption"},
							description:{
								"en-US":this.trackingVO.accessabilityOption
							}},
							id:AppConstants.NAVEX_URL +	statementDataVO.uniqueId + "/" + statementDataVO.courseRevision + "/" +	this.launchDataVO.clientKey, // Course ID on its own
							objectType: "Activity"
					},{
						definition:{
							name: {"en-US": "courseTopic"},
							description:{
								"en-US":this.trackingVO.courseTopic
							}},
							id:AppConstants.NAVEX_URL +	statementDataVO.uniqueId + "/" + statementDataVO.courseRevision + "/" +	this.launchDataVO.clientKey, // Course ID on its own
							objectType: "Activity"
					},{
						definition:{
							name: {"en-US": "learningType"},
							description:{
								"en-US":this.trackingVO.learningType
							}},
							id:AppConstants.NAVEX_URL +	statementDataVO.uniqueId + "/" + statementDataVO.courseRevision + "/" +	this.launchDataVO.clientKey, // Course ID on its own
							objectType: "Activity"
					},{
						definition:{
							name: {"en-US": "moduleVersion"},
							description:{
								"en-US":this.trackingVO.moduleVersion
							}},
							id:AppConstants.NAVEX_URL +	statementDataVO.uniqueId + "/" + statementDataVO.courseRevision + "/" +	this.launchDataVO.clientKey, // Course ID on its own
							objectType: "Activity"
					}]
				}
			} else {
				//page block
				if (statementDataVO.verb === this.COMPLETED) {
					// page 'completed'
					let attempts = "";
					this.trackingVO.attempts.forEach((value, i) => {
						if(i > 0)
							attempts = attempts+"[,]"+value.join(",");
						else
							attempts = value.join(",");
					})
					let failure = "";
					this.trackingVO.failure.forEach((value, i) => {
						if (i > 0) 
							failure = failure + "[,]" + value;
						else
							failure = value;
					});
					contextActivitiesJson = {
						grouping: [this.getActivitiesContent(statementDataVO).course],
						parent: [this.getActivitiesContent(statementDataVO).module],
						other:[{
									definition:{
										name: {"en-US": "attempts"},
										description:{
											"en-US":attempts
										}},
										id:AppConstants.NAVEX_URL +	statementDataVO.uniqueId + "/" + statementDataVO.courseRevision + "/" +	this.launchDataVO.clientKey, // Course ID on its own
										objectType: "Activity"
								},{
									definition:{
										name: {"en-US": "failure"},
										description:{
											"en-US":failure
										}},
										id:AppConstants.NAVEX_URL +	statementDataVO.uniqueId + "/" + statementDataVO.courseRevision + "/" +	this.launchDataVO.clientKey, // Course ID on its own
										objectType: "Activity"
								},{
									definition:{
										name: {"en-US": "moduleShortname"},
										description:{
											"en-US":this.trackingVO.moduleShortname
										}},
										id:AppConstants.NAVEX_URL +	statementDataVO.uniqueId + "/" + statementDataVO.courseRevision + "/" +	this.launchDataVO.clientKey, // Course ID on its own
										objectType: "Activity"
								}]
						};
				} else {
					// 'answered'
					contextActivitiesJson = {
						grouping: [
							this.getActivitiesContent(statementDataVO).course,
							this.getActivitiesContent(statementDataVO).module,
						],
						parent: [this.getActivitiesContent(statementDataVO).page],
					};
				}
			}
		}
		return contextActivitiesJson;
	}
	/**
	 * Common function that builds the contents of 'parent' and 'grouping' array of contextActivitiesJson
	 * @returns Object containing course, module and page as attributes used to construct 'parent' and 'grouping' array.
	 */
	private getActivitiesContent(statementDataVO) {
		let fullpageid = statementDataVO.moduleId + "/" + statementDataVO.pageId;
		let activitiesContent = { course: {}, module: {}, page: {} };
		let clientid = this.launchDataVO.clientKey
			? this.launchDataVO.clientKey
			: null;
		activitiesContent.course = {
			// Course
			definition: {
				name: {
					"en-US": statementDataVO.courseName, // Course name shown on screen
				},
			},
			id:
				AppConstants.NAVEX_URL +
				statementDataVO.uniqueId +
				"/" +
				statementDataVO.courseRevision +
				"/" +
				clientid, // Course ID on its own
			objectType: "Activity",
		};
		activitiesContent.module = {
			//Module
			definition: {
				name: {
					"en-US": statementDataVO.moduleId,
				},
			},
			id:
				AppConstants.NAVEX_URL +
				statementDataVO.uniqueId +
				"/" +
				statementDataVO.courseRevision +
				"/" +
				clientid +
				"/" +
				statementDataVO.moduleId, //Module ID
			objectType: "Activity",
		};
		activitiesContent.page = {
			//Page
			definition: {
				name: {
					"en-US": statementDataVO.pageId, // Page ID on its own
				},
			},
			id:
				AppConstants.NAVEX_URL +
				statementDataVO.uniqueId +
				"/" +
				statementDataVO.courseRevision +
				"/" +
				clientid +
				"/" +
				fullpageid, // Page ID with course information included
			objectType: "Activity",
		};
		return activitiesContent;
	}

	private courseCustomizerVersion() {
		return 0;
	}
	private getTimeStamp() {
		return new Date();
	}

	public getLaunchURLVO() {
		return this.launchURLVO;
	}

	public getLaunchDataVO() {
		return this.launchDataVO;
	}

	public getTrackingVO() {
		return this.trackingVO;
	}
}
